/**
 * CareDaily Goals API Service (read-only)
 *
 * Read-only helper for goals stored in CareDaily location state.
 * All goal mutations go through the ESP goalActivity endpoint
 * (GoalActivityService / goicon-goal-proxy), which lets the CareDaily
 * bot process the action and update location state itself.
 *
 * DO NOT add PUT /state or POST /appstore/stream calls here.
 * Goals are stored in location state named "goals":
 *
 * {
 *   "goal_1": {
 *     "goal_id": "goal_1",
 *     "title": "Test Goal 1",
 *     "category": "physical",
 *     "created_timestamp_ms": ...,
 *     "completed": false
 *   }
 * }
 */

import { CareDailyService, createCareDailyService } from './careDailyService';

export interface CareDailyGoal {
  goal_id: string;
  title: string;
  description: string;
  category: string;
  created_timestamp_ms: number;
  updated_timestamp_ms: number;
  completed: boolean;
  completed_timestamp_ms?: number;
  removed_timestamp_ms?: number;
  remove_after_ms?: number;
  user_id?: string; // Optional assignee
}

export interface GoalsLocationState {
  [goalId: string]: CareDailyGoal;
}

export interface GoalsTimeSeriesState {
  name: string;
  stateDate: string;
  stateDateMs: number;
  value: GoalsLocationState;
}

interface GoalsStateResponse {
  resultCode: number;
  value?: GoalsLocationState;
  stateDate?: string;
  stateDateMs?: number;
}

interface GoalsTimeSeriesResponse {
  resultCode: number;
  states: GoalsTimeSeriesState[];
}

class CareDailyGoalsService {
  private careDailyService: CareDailyService | null = null;

  /**
   * Initialize the service with facility ID to load credentials
   */
  async initialize(facilityId: string): Promise<void> {
    this.careDailyService = await createCareDailyService(facilityId);
  }

  /**
   * Get all goals for a location
   */
  async getGoals(locationId: string): Promise<CareDailyGoal[]> {
    if (!this.careDailyService) {
      throw new Error('CareDailyGoalsService not initialized. Call initialize() first.');
    }

    console.log(`🎯 Fetching goals from CareDaily for location: ${locationId}`);

    try {
      const response = await this.careDailyService.makeRequest<GoalsStateResponse>(
        `/locations/${locationId}/state`,
        'GET',
        { name: 'goals' }
      );

      if (!response.value || typeof response.value !== 'object') {
        console.log('📭 No goals found in CareDaily for this location');
        return [];
      }

      const goalsState = response.value as GoalsLocationState;
      const goals = Object.values(goalsState);

      console.log(`✅ Retrieved ${goals.length} goals from CareDaily`);
      return goals;
    } catch (error) {
      console.error('❌ Error fetching goals from CareDaily:', error);
      throw error;
    }
  }

  /**
   * Get goals history (timeseries)
   */
  async getGoalsHistory(
    locationId: string,
    startDate: number,
    endDate: number
  ): Promise<GoalsTimeSeriesState[]> {
    if (!this.careDailyService) {
      throw new Error('CareDailyGoalsService not initialized. Call initialize() first.');
    }

    console.log(`📜 Fetching goals history from CareDaily for location: ${locationId}`);

    try {
      const response = await this.careDailyService.makeRequest<GoalsTimeSeriesResponse>(
        `/locations/${locationId}/timeStates`,
        'GET',
        {
          name: 'goals',
          startDate: startDate.toString(),
          endDate: endDate.toString()
        }
      );

      const states = response.states || [];
      console.log(`✅ Retrieved ${states.length} historical goal states from CareDaily`);
      return states;
    } catch (error) {
      console.error('❌ Error fetching goals history from CareDaily:', error);
      throw error;
    }
  }

}

// Export a singleton instance
export const careDailyGoalsService = new CareDailyGoalsService();

